import { useRef, useState, useEffect } from 'react';
import {
  CircleHelp,
  AlertCircle,
  Clock,
  Loader2,
  BarChart3,
  ListChecks,
} from 'lucide-react';
import { useOnboardingStore } from '@/features/checklist-inicio/store/useOnboardingStore';
import { esEmpresaDemo } from '@/shared/demo';
import { solicitarInicioTour } from '@/shared/tour/motorTour';
import { registrarAyudaConsultada } from '@/shared/analitica/analitica';
import { EVENTO_ABRIR_CENTRO_AYUDA } from '@/shared/centro-ayuda/abrirCentroAyuda';
import type { ContextoCentroAyuda } from '@/shared/centro-ayuda/abrirCentroAyuda';
import { Tooltip } from '../../components/Tooltip';
import { Button } from '../../components/Button';
import { Drawer } from '../../components/Drawer';
import { TabBar } from '../../components/TabBar';
import type { TabItem } from '../../components/TabBar';
import { AttentionPulse } from '../../components/AttentionPulse/AttentionPulse';
import { Coachmark } from '../../components/Coachmark/Coachmark';
import { usarAyudaGuiada } from '@/shared/tour';
import { storageService } from '@/utils/storage';
import { configuracionService } from '@/services/api/configuraciones.service';
import { useHelpCenterCoachmark } from '@/hooks/useHelpCenterCoachmark';
import { useWorkspace } from '@/contexts/ContextoWorkspace';
import { useAuth } from '@/contexts/ContextoAuth';
import { estaHabilitadaPseMock, fechaHabilitacionPseMock, suscribirseAHabilitacionPseMock } from '@/shared/habilitacionPseMock';
import { freshdeskService } from '@/services/api/freshdesk.service';
import { toastBridge } from '@/services/api/toast-bridge';
import {
  ArticleDetail,
  ArticleList,
  GuidedHelpRow,
  HelpSearchBar,
  QuickCategoryLinks,
  ReporteAyudaTab,
  categoryAnchorId,
  useArticulosAyuda,
} from '@/features/centro-ayuda';
import { centroAyudaService } from '@/services/api/centroAyuda.service';

const DIAS_PULSO_AYUDA_TRAS_HABILITACION = 7;

export interface TourModulo {
  id: string;
  nombre: string;
}

export interface AyudaGuiadaMenuProps {
  onCambiar?: (activa: boolean) => void;
  version?: string;
  supportUrl?: string;
  onReportProblem?: () => void;
  systemOperational?: boolean;
  tourModulo?: TourModulo | null;
  tourProgreso?: { pasoActual: number; totalPasos: number };
  onIniciarTour?: (tourId: string) => void;
}

const TABS_BASE: TabItem[] = [
  { id: 'centro-ayuda', label: 'Centro de ayuda', icon: <CircleHelp /> },
];

const TAB_REPORTE: TabItem = { id: 'reporte-ayuda', label: 'Reporte', icon: <BarChart3 /> };

const leerEsAyudaGuiada = (): boolean => {
  const empresaId = storageService.getEmpresaId();
  if (!empresaId) return false;
  return storageService.getConfiguracionByEmpresa(empresaId)?.esAyudaGuiadaComprobante ?? false;
};

function SystemStatus({ operational }: { operational: boolean | undefined }) {
  if (operational === undefined) {
    return (
      <div className="flex items-center gap-1.5">
        <Loader2 size={11} className="animate-spin text-muted" />
        <span className="text-label text-muted">Verificando...</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <span
        className="inline-block w-[7px] h-[7px] rounded-full"
        style={{ background: operational ? 'var(--success)' : 'var(--error)' }}
      />
      <span className="text-label text-muted">
        {operational ? 'Servicios operativos' : 'Incidencia activa'}
      </span>
    </div>
  );
}

interface TourModuloSectionProps {
  tourModulo: TourModulo;
  tourProgreso?: { pasoActual: number; totalPasos: number };
  /** `desdeIndice` es el paso (0-index) por el que debe arrancar el motor del tour. */
  onIniciar: (desdeIndice: number) => void;
}

function TourModuloSection({ tourModulo, tourProgreso, onIniciar }: TourModuloSectionProps) {
  const totalPasos = tourProgreso?.totalPasos ?? 0;
  const pasoActual = tourProgreso?.pasoActual ?? -1;
  const iniciado = pasoActual >= 0 && totalPasos > 0;
  const completado = iniciado && pasoActual >= totalPasos - 1;
  const enProgreso = iniciado && !completado;
  const porcentaje = iniciado ? Math.min(((pasoActual + 1) / totalPasos) * 100, 100) : 0;

  const titulo = completado
    ? `Tour completado: ${tourModulo.nombre}`
    : iniciado
      ? `Tour en curso: ${tourModulo.nombre}`
      : `Tour disponible: ${tourModulo.nombre}`;

  const etiquetaBadge = !iniciado ? 'Nuevo' : `${Math.min(pasoActual + 1, totalPasos)}/${totalPasos}`;
  const etiquetaBoton = completado
    ? 'Ver de nuevo'
    : iniciado
      ? `Continuar en el paso ${pasoActual + 1}`
      : 'Iniciar tour';

  const handleAccionPrincipal = () => onIniciar(enProgreso ? pasoActual : 0);
  const handleRepasar = () => onIniciar(0);

  return (
    <div
      className="mb-5 rounded-lg border p-4"
      style={{
        background: 'linear-gradient(135deg, var(--primary-light) 0%, var(--surface-0) 70%)',
        borderColor: 'var(--primary-light)',
      }}
    >
      <div className="mb-0.5 flex items-center justify-between gap-2">
        <p className="flex min-w-0 items-center gap-1.5 text-caption-medium font-bold text-primary">
          <Clock size={14} className="shrink-0 text-brand" />
          <span className="truncate">{titulo}</span>
        </p>
        <span className="shrink-0 text-micro font-semibold text-brand">{etiquetaBadge}</span>
      </div>

      <div className="my-2 h-1.5 overflow-hidden rounded-full bg-white/60">
        <div
          className="h-full rounded-full transition-[width] duration-300"
          style={{ width: `${porcentaje}%`, background: completado ? 'var(--success)' : 'var(--brand)' }}
        />
      </div>

      <div className="flex items-center justify-between gap-3">
        <Button
          variant="primary"
          size="sm"
          className={enProgreso ? 'flex-1 max-w-[75%]' : 'flex-1'}
          onClick={handleAccionPrincipal}
        >
          {etiquetaBoton}
        </Button>
        {enProgreso && (
          <button
            type="button"
            onClick={handleRepasar}
            className="shrink-0 text-label font-medium text-tertiary underline hover:text-secondary"
          >
            Repasar
          </button>
        )}
      </div>
    </div>
  );
}

export const AyudaGuiadaMenu = ({
  onCambiar,
  version = '1.0.0',
  supportUrl = 'https://portalemprendedores.contasis.com/support/home',
  onReportProblem,
  systemOperational = true,
  tourModulo = null,
  tourProgreso,
  onIniciarTour,
}: AyudaGuiadaMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [tabActivo, setTabActivo] = useState('centro-ayuda');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { cambiarAyudaActivada, reiniciarAyudaGuiada } = usarAyudaGuiada();
  const { currentEmpresa } = useWorkspace();
  const { user } = useAuth();
  // Solo si el checklist de inicio aplica a esta empresa: real (no demo), habilitada por PSE
  // (si no, el checklist nunca se muestra), con datos cargados y sin primera venta.
  const checklistAplica = useOnboardingStore(
    (s) =>
      !!currentEmpresa &&
      !esEmpresaDemo(currentEmpresa.ruc) &&
      !!s.progress[currentEmpresa.id] &&
      !s.isRetired(currentEmpresa.id),
  );
  const mostrarChecklist = checklistAplica && !!currentEmpresa && estaHabilitadaPseMock(currentEmpresa.ruc);
  const { articulos, categorias } = useArticulosAyuda();
  const tabs = user?.esSuperAdminSenciyo ? [...TABS_BASE, TAB_REPORTE] : TABS_BASE;
  const [fechaHabilitacion, setFechaHabilitacion] = useState(() =>
    currentEmpresa?.ruc ? fechaHabilitacionPseMock(currentEmpresa.ruc) : null
  );
  const { pulseActive, coachmarkOpen, dismissCoachmark, closeCoachmark, handleDirectClick } = useHelpCenterCoachmark(
    user?.id,
    fechaHabilitacion,
    DIAS_PULSO_AYUDA_TRAS_HABILITACION
  );
  const coachmarkAnchorRef = useRef<HTMLDivElement>(null);

  // Clic afuera del botón/cartelito: lo cierra sin marcarlo como visto.
  useEffect(() => {
    if (!coachmarkOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!coachmarkAnchorRef.current?.contains(e.target as Node)) {
        closeCoachmark();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [coachmarkOpen, closeCoachmark]);

  useEffect(() => {
    const actualizarHabilitacion = () => {
      setFechaHabilitacion(currentEmpresa?.ruc ? fechaHabilitacionPseMock(currentEmpresa.ruc) : null);
    };
    actualizarHabilitacion();
    return suscribirseAHabilitacionPseMock(actualizarHabilitacion);
  }, [currentEmpresa?.ruc]);

  const [esAyudaGuiada, setEsAyudaGuiada] = useState(leerEsAyudaGuiada);

  useEffect(() => {
    const sincronizar = () => {
      setEsAyudaGuiada(leerEsAyudaGuiada());
      // Sincronizar el motor del tour con la configuración de la empresa actual
      const empresaId = storageService.getEmpresaId();
      if (!empresaId) return;
      const config = storageService.getConfiguracionByEmpresa(empresaId);
      if (config && typeof config.esAyudaGuiadaComprobante === 'boolean') {
        cambiarAyudaActivada(config.esAyudaGuiadaComprobante);
      }
    };

    sincronizar(); // sincronizar al montar con la empresa activa
    window.addEventListener('senciyo:workspace-updated', sincronizar);
    return () => window.removeEventListener('senciyo:workspace-updated', sincronizar);
  }, [cambiarAyudaActivada]);

  useEffect(() => {
    const abrirDesdeEvento = (evento: Event) => {
      const { busqueda = '' } = (evento as CustomEvent<ContextoCentroAyuda>).detail ?? {};
      setTabActivo('centro-ayuda');
      setSelectedArticleId(null);
      setSearchTerm(busqueda);
      setIsOpen(true);
    };

    window.addEventListener(EVENTO_ABRIR_CENTRO_AYUDA, abrirDesdeEvento);
    return () => window.removeEventListener(EVENTO_ABRIR_CENTRO_AYUDA, abrirDesdeEvento);
  }, []);

  useEffect(() => {
    if (isOpen && tabActivo === 'centro-ayuda' && !selectedArticleId) {
      searchInputRef.current?.focus();
    }
  }, [isOpen, tabActivo, selectedArticleId]);

  const handleToggle = (valor: boolean) => {
    if (cargando) return;
    setEsAyudaGuiada(valor);
    const config = storageService.getConfiguracionActual();
    if (config?.id && config.empresaId) {
      storageService.updateConfiguracionEmpresa(config.empresaId, {
        esAyudaGuiadaComprobante: valor,
      });
      setCargando(true);
      void configuracionService
        .update(config.id, { empresaId: config.empresaId, esAyudaGuiadaComprobante: valor })
        .finally(() => setCargando(false));
    }
    if (valor) {
      reiniciarAyudaGuiada();
    } else {
      cambiarAyudaActivada(false);
    }
    onCambiar?.(valor);
  };

  const handleIniciarTour = (desdeIndice: number) => {
    if (!tourModulo) return;
    registrarAyudaConsultada({
      tipoAyuda: 'tour',
      origen: 'header',
      modulo: tourModulo.id,
      tourId: tourModulo.id,
      recurso: 'centro_ayuda',
    });
    solicitarInicioTour(tourModulo.id, { desdeIndice });
    onIniciarTour?.(tourModulo.id);
    setIsOpen(false);
  };

  const handleClosePanel = () => {
    setIsOpen(false);
    setSearchTerm('');
    setSelectedArticleId(null);
  };

  const handleMostrarChecklist = () => {
    if (!currentEmpresa) return;
    useOnboardingStore.getState().restore(currentEmpresa.id);
    handleClosePanel();
  };

  const handleOpenArticle = (articleId: string) => {
    registrarAyudaConsultada({ tipoAyuda: 'recurso', origen: 'panel' });
    setSelectedArticleId(articleId);
  };

  const handleContactSupport = async () => {
    try {
      await freshdeskService.openInNewTab();
    } catch (e) {
      console.error('Error al redirigir a Freshdesk:', e);
      toastBridge.error('No se pudo abrir el portal de soporte. Intenta nuevamente.', 'freshdesk-redirect');
    }
  };

  const handleJumpToCategory = (category: string) => {
    document.getElementById(categoryAnchorId(category))?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handleFeedback = (articuloId: string, esUtil: boolean) => {
    void centroAyudaService.enviarFeedback({ articuloId, esUtil });
  };

  const selectedArticle = articulos.find((a) => a.id === selectedArticleId) ?? null;
  const isSearching = searchTerm.trim() !== '';

  return (
    <>
      <div className="relative" ref={coachmarkAnchorRef}>
        <Tooltip text="Centro de ayuda" position="bottom" disabled={coachmarkOpen}>
          <AttentionPulse active={pulseActive}>
            <Button
              iconOnly
              variant="ghost"
              size="md"
              aria-label="Centro de ayuda"
              onClick={() => {
                setIsOpen(prev => !prev);
                handleDirectClick();
              }}
              active={isOpen}
              icon={<CircleHelp className="w-[18px] h-[18px]" />}
            />
          </AttentionPulse>
        </Tooltip>

        <Coachmark
          open={coachmarkOpen}
          title="¿Necesitas ayuda?"
          body={
            <>
              Encuentra respuestas a tus dudas y aprende a usar <strong>Negocios Online</strong> paso a paso.
            </>
          }
          onDismiss={dismissCoachmark}
        />
      </div>

      <Drawer
        isOpen={isOpen}
        onClose={handleClosePanel}
        title="Centro de ayuda"
        size="sm"
        tabBar={
          <TabBar
            items={tabs}
            activeTab={tabActivo}
            onChange={setTabActivo}
            variant="underline"
          />
        }
        footerTop={
          <div className="flex items-center justify-between">
            <span className="text-label text-muted">v{version} · Negocios Online</span>
            <SystemStatus operational={systemOperational} />
          </div>
        }
      >
        <div className="flex h-full flex-col space-y-6">
          {tabActivo === 'centro-ayuda' && (
            <div className="flex min-h-0 flex-1 flex-col">
              {selectedArticle ? (
                <div className="min-h-0 flex-1 overflow-y-auto">
                  <ArticleDetail article={selectedArticle} onBack={() => setSelectedArticleId(null)} onFeedback={handleFeedback} />
                </div>
              ) : (
                <>
                  <div
                    className={
                      'flex-shrink-0 overflow-hidden transition-[max-height,opacity] duration-200 ' +
                      (isSearching
                        ? 'max-h-0 opacity-0'
                        : 'max-h-[600px] opacity-100 border-b border-muted mb-5')
                    }
                  >
                    <GuidedHelpRow enabled={esAyudaGuiada} onToggle={handleToggle} disabled={cargando} />

                    {esAyudaGuiada && tourModulo && (
                      <TourModuloSection
                        tourModulo={tourModulo}
                        tourProgreso={tourProgreso}
                        onIniciar={handleIniciarTour}
                      />
                    )}

                    {mostrarChecklist && (
                      <div className="pb-5">
                        <button
                          type="button"
                          onClick={handleMostrarChecklist}
                          className="flex w-full items-center gap-2 rounded-lg border border-muted px-3 py-2.5 text-left text-ui-sm font-medium text-primary transition-colors hover:bg-surface-1"
                        >
                          <ListChecks className="h-4 w-4 shrink-0 text-brand" />
                          Mostrar checklist de inicio
                        </button>
                      </div>
                    )}
                  </div>

                  <p className="mb-3 flex-shrink-0 text-label font-bold uppercase tracking-wide text-subtle">
                    Buscar en el centro de ayuda
                  </p>
                  <HelpSearchBar ref={searchInputRef} value={searchTerm} onChange={setSearchTerm} />
                  <QuickCategoryLinks categories={categorias} onJump={handleJumpToCategory} visible={!isSearching} />
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    <ArticleList
                      articles={articulos}
                      searchTerm={searchTerm}
                      onOpenArticle={handleOpenArticle}
                      onContactSupport={handleContactSupport}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {tabActivo === 'reporte-ayuda' && <ReporteAyudaTab />}

          {/* Base de conocimiento — oculto hasta MVP 2
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div
                className="flex items-center justify-center shrink-0 bg-primary-light"
                style={{ width: 38, height: 38, borderRadius: 10 }}
              >
                <BookOpen size={19} className="text-primary" />
              </div>
              <div>
                <p className="m-0 text-ui-sm font-semibold text-primary mb-1">Base de conocimiento</p>
                <p className="m-0 text-caption text-tertiary">Artículos, tutoriales y guías SUNAT.</p>
              </div>
            </div>
            <Button
              variant="primary"
              size="md"
              fullWidth
              icon={<ExternalLink size={14} />}
              iconPosition="right"
              onClick={() => window.open(supportUrl, '_blank', 'noopener,noreferrer')}
            >
              Ir al portal de soporte
            </Button>
          </div>
          */}

          {/* Reportar un problema — oculto hasta MVP 2
          <div>
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              icon={<AlertCircle size={15} />}
              onClick={onReportProblem}
              className="border border-secondary"
            >
              Reportar un problema
            </Button>
          </div>
          */}
        </div>
      </Drawer>
    </>
  );
};

